import { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import {
    Box,
    IconButton,
    Tooltip,
    HStack,
    useDisclosure,
    Flex,
} from '@chakra-ui/react';
import NumberFormatCustom from 'components/maintenance/NumberFormatCustom';
import { useNavigate } from 'react-router-dom';
import path from '@/routes/constants';
import { Button } from 'components/v4';
import { BsThreeDotsVertical } from 'react-icons/bs';
import FormBase from '@/components/forms/new/FormBase';
import NumberBlockSizeField from './formFields/NumberBlockSizeField';
import NumberBlockBillField from './formFields/NumberBlockBillField';
import NumberBlockSetupFeeField from './formFields/NumberBlockSetupFeeField';
import NumberBlockRateField from './formFields/NumberBlockRateField';
import PrefixPatternTypeField from './formFields/PrefixPatternTypeField';
import PrefixPatternInputField from './formFields/PrefixPatternInputField';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

/**
 * Component for The number block tab inside Plans page
 */
const NumberBlockRates = ({ child }) => {
    const tableRef = useRef();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const { child } = useSelector((state) => ({ ...state.table }));
    const [action, setAction] = useState('GET'); // GET | ADD | EDIT
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [itemToDelete, setItemToDelete] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Fetch number block rates data using TanStack Query
    const {
        data: ratesData = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['numberBlockRates', child?.id],
        queryFn: () => fetchNumberBlockRates(child?.id),
        enabled: !!child?.id,
    });
    const { darkMode } = useSelector((state) => state.settings);
    // AGGrid columns
    const columns = [
        { headerName: 'Block Size', field: 'size', flex: 1 },
        {
            headerName: 'Bill',
            field: 'bill',
            flex: 1,
            cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
            headerName: 'Setup Fee',
            field: 'setupFee',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        {
            headerName: 'Rate',
            field: 'rate',
            flex: 1,
            cellRenderer: (params) => (
                <NumberFormatCustom displayType="text" value={params.value} />
            ),
        },
        { headerName: 'Prefix', field: 'prefix', flex: 1 },
        { headerName: 'Pattern', field: 'pattern', flex: 1 },
        {
            headerName: 'Action',
            lockPosition: 'right',
            width: 120,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => (
                <Tooltip label="Edit">
                    <IconButton
                        icon={
                            <BsThreeDotsVertical
                                fill={darkMode ? 'white' : 'black'}
                            />
                        }
                        aria-label="Edit"
                        variant="ghost"
                        onClick={() => handleEdit(params.data)}
                        _hover={{
                            bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                        }}
                        _active={{
                            bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                        }}
                        _focus={{ boxShadow: 'none' }}
                    />
                </Tooltip>
            ),
        },
    ];

    // Handlers
    const handleCreate = () => {
        setAction('ADD');
        setEditData(null);
        setApiError('');
    };
    const handleEdit = (row) => {
        setAction('EDIT');
        // Determine prefixType based on which field has a value (lowercase for consistency)
        const prefixType = row.prefix
            ? 'prefix'
            : row.pattern
              ? 'pattern'
              : 'prefix';
        setEditData({
            ...row,
            prefixType: prefixType,
        });
        setApiError('');
    };
    const handleDelete = async (row) => {
        setItemToDelete(row);
        onOpen();
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await axios.put('/numberblockrate', {
                ...itemToDelete,
                id: itemToDelete.id,
                isActive: false,
            });
            refetch();
            setAction('GET');
            onClose();
        } catch (err) {
            setApiError('Failed to delete number block rate.');
        } finally {
            setIsDeleting(false);
            setItemToDelete(null);
        }
    };
    const validateForm = (values) => {
        const errors = {};
        if (!values.size) errors.size = 'Block Size is required';
        // if (values.prefixType === 'Prefix' && !values.prefix) errors.prefix = 'Prefix is required';
        // if (values.prefixType === 'Pattern' && !values.pattern) errors.pattern = 'Pattern is required';
        return errors;
    };
    const handleSave = async (values) => {
        setApiError('');
        setIsSaving(true);
        try {
            // Remove empty/null/undefined values
            const payload = Object.fromEntries(
                Object.entries(values).filter(
                    ([, v]) => v !== '' && v !== null && v !== undefined,
                ),
            );

            // Based on prefixType, only send the relevant field
            if (values.prefixType === 'prefix') {
                // If prefix type, remove pattern from payload
                delete payload.pattern;
            } else if (values.prefixType === 'pattern') {
                // If pattern type, remove prefix from payload
                delete payload.prefix;
            }

            // Remove prefixType from payload as it's only for UI logic
            delete payload.prefixType;

            if (action === 'ADD') {
                await axios.post('/numberblockrate', {
                    ...payload,
                    isActive: true,
                });
            } else {
                await axios.put('/numberblockrate', {
                    ...payload,
                    id: values.id,
                });
            }
            setAction('GET');
            setEditData(null);
            refetch();
        } catch (err) {
            setApiError(err?.response?.data?.message || 'Error saving data');
        } finally {
            setIsSaving(false);
        }
    };
    const handleCancel = () => {
        setAction('GET');
        setEditData(null);
        setApiError('');
    };
    const breadCrumbTrail = [
        { label: 'Number Block Rates', path: ['', { index: 0 }] },
        { label: 'Add Number Block Rate', path: '' },
    ];
    if (action !== 'GET') {
        const defaultInitialValues = {
            size: '',
            bill: true,
            setupFee: '',
            rate: '',
            prefix: '',
            pattern: '',
            prefixType: 'prefix',
            planID: child?.id || null,
        };
        return (
            <>
                <BreadcrumbTrail
                    breadCrumbTrail={breadCrumbTrail}
                    handleBackButton={handleCancel}
                />
                <FormBase
                    //header={action === 'EDIT' ? 'Edit Number Block Rate' : 'Add Number Block Rate'}
                    subTitleHeader={false}
                    initialValues={editData || defaultInitialValues}
                    isLoading={isSaving}
                    finalFormSubmit={handleSave}
                    onBack={handleCancel}
                    customClick={true}
                    validate={validateForm}
                    extraButton={
                        action === 'EDIT' && (
                            <Flex justify="flex-end" width="100%">
                                <Button
                                    w={142}
                                    colorScheme="red"
                                    onClick={() => handleDelete(editData)}
                                    isDisabled={isDeleting || isSaving}>
                                    Delete
                                </Button>
                            </Flex>
                        )
                    }>
                    {({
                        errors = {},
                        touched = {},
                        values = {},
                        setFieldValue,
                    }) =>
                        [
                            apiError && (
                                <Box key="error" color="red.500" mb={2}>
                                    {apiError}
                                </Box>
                            ),
                            <NumberBlockSizeField
                                key="size"
                                errors={errors}
                                touched={touched}
                                values={values}
                                darkMode={darkMode}
                                meta={{ setFieldValue }}
                            />,
                            <NumberBlockBillField
                                key="bill"
                                errors={errors}
                                touched={touched}
                                values={values}
                                darkMode={darkMode}
                                meta={{ setFieldValue }}
                            />,
                            <NumberBlockSetupFeeField
                                key="setupFee"
                                errors={errors}
                                touched={touched}
                                values={values}
                                darkMode={darkMode}
                                meta={{ setFieldValue }}
                            />,
                            <NumberBlockRateField
                                key="rate"
                                errors={errors}
                                touched={touched}
                                values={values}
                                darkMode={darkMode}
                                meta={{ setFieldValue }}
                            />,
                            <PrefixPatternTypeField
                                key="prefixType"
                                errors={errors}
                                touched={touched}
                                values={values}
                                darkMode={darkMode}
                                meta={{ setFieldValue }}
                            />,
                            (values?.prefixType ||
                                defaultInitialValues.prefixType) ===
                                'prefix' && (
                                <PrefixPatternInputField
                                    key="prefix"
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    darkMode={darkMode}
                                    type="prefix"
                                    meta={{ setFieldValue }}
                                />
                            ),
                            (values?.prefixType ||
                                defaultInitialValues.prefixType) ===
                                'pattern' && (
                                <PrefixPatternInputField
                                    key="pattern"
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    darkMode={darkMode}
                                    type="pattern"
                                    meta={{ setFieldValue }}
                                />
                            ),
                        ].filter(Boolean)
                    }
                </FormBase>

                <DeleteConfirmationModal
                    isOpen={isOpen}
                    onClose={onClose}
                    itemName={
                        itemToDelete?.size
                            ? `Number Block Rate (Size: ${itemToDelete.size})`
                            : 'Number Block Rate'
                    }
                    onConfirm={confirmDelete}
                    isDeleting={isDeleting}
                    itemType="Number Block Rate"
                />
            </>
        );
    }
    return (
        <Box style={{ minHeight: '400px', overflowY: 'auto' }}>
            <AGGridMain
                gridRef={tableRef}
                tableData={{ columns, data: ratesData }}
                refetch={refetch}
                isLoading={isLoading}
                titleName="Number Block Rates"
                extraComponents={
                    <HStack spacing={2} paddingLeft={5}>
                        <Tooltip
                            label="CSV Update"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="110px"
                                    onClick={() => {
                                        const urlPathname = `/plans/${path.CSVNUMBERBLOCKRATES}?numberBlockRate=${encodeURIComponent(
                                            child?.name?.replace(/ /g, '_'),
                                        )}&planID=${encodeURIComponent(child?.id)}`;
                                        dispatch({
                                            type: 'UPDATE_HISTORY',
                                            payload: {
                                                pathname: urlPathname,
                                            },
                                        });
                                        navigate(urlPathname?.split('&')[0]);
                                    }}>
                                    CSV Update
                                </Button>
                            </div>
                        </Tooltip>
                        <Tooltip
                            label="Add Rates"
                            placement="bottom"
                            hasArrow
                            portalProps={{ containerRef: undefined }}>
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                }}>
                                <Button
                                    size="sm"
                                    width="120px"
                                    onClick={handleCreate}>
                                    + Rates
                                </Button>
                            </div>
                        </Tooltip>
                    </HStack>
                }
            />
            {error && (
                <Box color="red.500">
                    Error loading number block rates data.
                </Box>
            )}

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                itemName={
                    itemToDelete?.size
                        ? `Number Block Rate (Size: ${itemToDelete.size})`
                        : 'Number Block Rate'
                }
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
                itemType="Number Block Rate"
            />
        </Box>
    );
};

const fetchNumberBlockRates = async (planId) => {
    if (!planId) return [];
    const response = await axios.get(`/numberblockrates/${planId}`);
    return response.data;
};

export default NumberBlockRates;
